import { useGetCandidateActions } from "@api";
import { Alert } from "@mui/material";
import { useI18nContext } from "@root/utils/organisation";
import { useEffect } from "react";
import { type Control, useWatch } from "react-hook-form";
import type { IModalActionForm, IModalActionResolver, TModalAction } from "../../logics/types";
import { getSlotDayRange, hasSlotConflict } from "./logics";

export const SLOT_CONFLICT_MESSAGE = "Un rendez-vous existe déjà pour ce candidat à la même date et à la même heure.";

export interface IModalActionFormSlotConflictAlertProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
  action?: TModalAction;
}

// Warns (without blocking) when the candidate already has an action starting at the chosen date and time.
export const ModalActionFormSlotConflictAlert = ({ control, action }: IModalActionFormSlotConflictAlertProps) => {
  const { timeZone } = useI18nContext();
  const [candidate, date, startTime] = useWatch({ control, name: ["candidate", "date", "startTime"] });
  const [{ data }, getCandidateActions, cancel] = useGetCandidateActions();

  const candidateId = candidate?._id;
  const dateTime = date?.getTime();

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when the day or the candidate changes
  useEffect(() => {
    if (!candidateId || !date || startTime === "") {
      return;
    }
    getCandidateActions(candidateId, getSlotDayRange(date, timeZone)).catch(() => undefined);
    return cancel;
  }, [candidateId, dateTime, startTime === "", timeZone]);

  if (!candidateId || !date || startTime === "" || !data) {
    return null;
  }

  const conflict = hasSlotConflict(data.data, { date, startTime, timeZone, currentActionId: action?._id });
  if (!conflict) {
    return null;
  }
  return <Alert severity="error">{SLOT_CONFLICT_MESSAGE}</Alert>;
};
